import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Building2, Lock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** The invited organization administrator sets their own password and lands in their console. */
export default function OrgActivatePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [f, setF] = useState({ full_name: '', password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/orgs/activation/${token}`)
      .then(r => { setInfo(r.data); setF(v => ({ ...v, full_name: r.data.full_name || '' })); })
      .catch(e => setError(e?.response?.data?.detail || 'That invitation link is no longer valid'));
  }, [token]);

  const submit = async () => {
    if (f.password.length < 8) return toast.error('Use at least 8 characters');
    if (f.password !== f.confirm) return toast.error("Those passwords don't match");
    setSaving(true);
    try {
      const r = await axios.post(`${API}/orgs/activation/${token}`,
        { password: f.password, full_name: f.full_name });
      localStorage.setItem('futurepath_token', r.data.access_token);
      window.location.assign('/app/org');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not activate that account');
    } finally { setSaving(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-navy-gradient flex items-center justify-center p-6">
        <div className="max-w-md text-center" data-testid="activate-error">
          <Lock className="w-8 h-8 text-rose-300 mx-auto" />
          <p className="text-white mt-4">{error}</p>
          <p className="text-sm text-slate-300 mt-2">
            Ask your FuturePath contact to send a new invitation.
          </p>
        </div>
      </div>
    );
  }
  if (!info) return <div className="min-h-screen bg-navy-gradient" />;

  const accent = info.branding?.primary_color || '#22d3ee';

  return (
    <div className="min-h-screen bg-navy-gradient flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-slate-900/70 backdrop-blur-xl p-7 md:p-9"
        data-testid="org-activate-page">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl border border-white/15 flex items-center justify-center overflow-hidden"
            style={{ background: `${accent}18` }}>
            {info.branding?.logo_url
              ? <img src={info.branding.logo_url} alt="" className="w-full h-full object-contain p-1" />
              : <Building2 className="w-5 h-5" style={{ color: accent }} />}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: accent }}>
              {(info.role || 'administrator').replace(/_/g, ' ')}
            </p>
            <p className="text-white font-semibold">{info.organization}</p>
          </div>
        </div>

        <h1 className="font-outfit text-3xl font-semibold text-white mt-6">Set your password.</h1>
        <p className="text-sm text-slate-300 mt-2">
          Your organization's FuturePath instance is ready. Create a password for{' '}
          <span className="text-slate-200">{info.email}</span> and you'll land straight in your
          console, your branding, your team, your places.
        </p>

        <div className="space-y-4 mt-6">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Your name</Label>
            <Input value={f.full_name} onChange={e => setF({ ...f, full_name: e.target.value })}
              className="input-dark" data-testid="activate-name" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Password</Label>
            <Input type="password" value={f.password} onChange={e => setF({ ...f, password: e.target.value })}
              className="input-dark" data-testid="activate-password" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Confirm password</Label>
            <Input type="password" value={f.confirm} onChange={e => setF({ ...f, confirm: e.target.value })}
              className="input-dark" data-testid="activate-confirm" />
          </div>
        </div>

        <Button onClick={submit} disabled={saving} data-testid="activate-submit"
          className="w-full rounded-full font-semibold text-slate-900 mt-6"
          style={{ background: accent }}>
          {saving ? 'Setting up…' : <>Create my password <ArrowRight className="w-4 h-4 ml-1.5" /></>}
        </Button>
        <p className="text-xs text-slate-300 mt-4">
          {info.branding?.powered_by_text || 'Powered by Agentic FuturePath AI'} · your portal will be
          at agenticfuturepath.com/{info.slug}
        </p>
      </motion.div>
    </div>
  );
}
